import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Users, UserPlus, Copy, CheckCircle, Clock, XCircle, PauseCircle,
  ArrowClockwise, EnvelopeSimple,
} from '@phosphor-icons/react';
import { API } from '@/config/api';

/* --------------------------------------------------------------------------
 * Sub-Agents card — parent recruiters only.
 * Lists every sub-agent this recruiter has invited, with status pills and an
 * "Invite sub-agent" flow that shows the temp password once (also emailed).
 * ------------------------------------------------------------------------- */
const STATUS_META = {
  pending:   { label: 'Awaiting admin approval', tone: 'bg-amber-50 text-amber-800 border-amber-200',   Icon: Clock },
  active:    { label: 'Active',                  tone: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle },
  suspended: { label: 'Suspended',               tone: 'bg-rose-50 text-rose-700 border-rose-200',      Icon: PauseCircle },
  rejected:  { label: 'Rejected',                tone: 'bg-gray-100 text-gray-600 border-gray-200',     Icon: XCircle },
};

function randomTempPassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 12; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function SubAgentsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', temp_password: randomTempPassword() });
  const [submitting, setSubmitting] = useState(false);
  const [justCreated, setJustCreated] = useState(null); // { email, temp_password, invite_email_sent }

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/recruiter/sub-agents`);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      // Non-parent recruiters get a 403 here — silently hide.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || (form.temp_password || '').length < 8) {
      toast.error('Fill name, email and a temporary password of at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/recruiter/sub-agents`, form);
      setJustCreated({
        email: data.email,
        temp_password: form.temp_password,
        invite_email_sent: !!data.invite_email_sent,
      });
      setForm({ name: '', email: '', temp_password: randomTempPassword() });
      setInviteOpen(false);
      await load();
      toast.success('Sub-agent invited — pending admin approval');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to invite');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuspend = async (row) => {
    if (!window.confirm(`Suspend "${row.name}"? They won't be able to log in until you reactivate them.`)) return;
    try {
      await axios.patch(`${API}/recruiter/sub-agents/${row.id}`, { sub_agent_status: 'suspended' });
      toast.success('Sub-agent suspended');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to suspend');
    }
  };

  const handleReactivate = async (row) => {
    try {
      await axios.patch(`${API}/recruiter/sub-agents/${row.id}`, { sub_agent_status: 'active' });
      toast.success('Sub-agent reactivated');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reactivate');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove "${row.name}"? This deletes their access and cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/recruiter/sub-agents/${row.id}`);
      toast.success('Sub-agent removed');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove');
    }
  };

  const copyCreds = () => {
    if (!justCreated) return;
    const text = `Email: ${justCreated.email}\nTemporary password: ${justCreated.temp_password}`;
    navigator.clipboard?.writeText(text);
    toast.success('Credentials copied to clipboard');
  };

  if (loading) {
    return (
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6" data-testid="sub-agents-card-loading">
        <div className="h-6 w-40 bg-gray-100 rounded animate-pulse mb-4" />
        <div className="h-16 w-full bg-gray-50 rounded animate-pulse" />
      </section>
    );
  }

  return (
    <>
      <section className="bg-white border border-gray-200 overflow-hidden" data-testid="sub-agents-card">
        <div className="px-6 lg:px-8 py-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">Team</p>
            <h2 className="text-lg font-semibold text-gray-900 mt-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>My Sub-Agents</h2>
            <p className="text-[11px] text-gray-500 mt-1 max-w-md">
              Invite team members to add students under your account. Each invite needs admin approval before they can log in.
            </p>
          </div>
          <Button onClick={() => setInviteOpen(true)} className="bg-gray-900 hover:bg-black text-white h-10 rounded-md px-4 text-[12px] font-medium" data-testid="invite-sub-agent-btn">
            <UserPlus size={14} className="mr-2" /> Invite Sub-Agent
          </Button>
        </div>

        {rows.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-full border border-gray-200 bg-white flex items-center justify-center mb-4">
              <Users size={20} className="text-gray-500" />
            </div>
            <p className="text-[15px] font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>No sub-agents yet</p>
            <p className="text-[12px] text-gray-500 mt-2 mb-5 max-w-sm mx-auto">
              Growing your team? Invite trusted colleagues to add students under your account. You&rsquo;ll still see everything they submit.
            </p>
            <Button onClick={() => setInviteOpen(true)} variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md text-[12px] font-medium" data-testid="empty-invite-sub-agent-btn">
              <UserPlus size={13} className="mr-2" /> Invite your first sub-agent
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {rows.map(row => {
              const meta = STATUS_META[row.sub_agent_status] || STATUS_META.pending;
              const StatusIcon = meta.Icon;
              return (
                <div key={row.id} className="px-5 lg:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between" data-testid={`sub-agent-row-${row.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{row.name}</p>
                    <p className="text-xs text-gray-500 truncate flex items-center gap-1.5"><EnvelopeSimple size={11} /> {row.email}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full border ${meta.tone}`} data-testid={`sub-agent-status-${row.id}`}>
                      <StatusIcon size={12} weight="fill" /> {meta.label}
                    </span>
                    {row.sub_agent_status === 'active' && (
                      <Button size="sm" variant="ghost" className="h-8 text-xs text-rose-600 hover:bg-rose-50" onClick={() => handleSuspend(row)} data-testid={`suspend-sub-agent-${row.id}`}>
                        <PauseCircle size={14} className="mr-1" /> Suspend
                      </Button>
                    )}
                    {row.sub_agent_status === 'suspended' && (
                      <Button size="sm" variant="ghost" className="h-8 text-xs text-emerald-700 hover:bg-emerald-50" onClick={() => handleReactivate(row)} data-testid={`reactivate-sub-agent-${row.id}`}>
                        <ArrowClockwise size={14} className="mr-1" /> Reactivate
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-8 text-xs text-gray-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(row)} data-testid={`delete-sub-agent-${row.id}`}>
                      Remove
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-sub-agent-dialog">
          <DialogHeader>
            <DialogTitle>Invite a sub-agent</DialogTitle>
            <DialogDescription>
              We&rsquo;ll email your invitee their temporary credentials. You&rsquo;ll also see them once on screen so you can share them privately. Admin approval is required before they can log in.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sa-name">Full name *</Label>
              <Input id="sa-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Jane Doe" required data-testid="sa-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sa-email">Email *</Label>
              <Input id="sa-email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@youragency.com" required data-testid="sa-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sa-pwd">Temporary password *</Label>
              <div className="flex gap-2">
                <Input id="sa-pwd" value={form.temp_password} onChange={e => setForm(p => ({ ...p, temp_password: e.target.value }))} minLength={8} required data-testid="sa-pwd-input" />
                <Button type="button" variant="outline" onClick={() => setForm(p => ({ ...p, temp_password: randomTempPassword() }))} data-testid="sa-pwd-regen">Regenerate</Button>
              </div>
              <p className="text-[11px] text-gray-500">Min 8 characters. They&rsquo;ll be prompted to change it on first login.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="sa-invite-submit-btn">
                {submitting ? 'Sending invite…' : 'Send Invite'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Post-invite credentials reveal */}
      <Dialog open={!!justCreated} onOpenChange={(o) => { if (!o) setJustCreated(null); }}>
        <DialogContent data-testid="sa-credentials-dialog">
          <DialogHeader>
            <DialogTitle>Invite sent</DialogTitle>
            <DialogDescription>
              {justCreated?.invite_email_sent
                ? "We've emailed the credentials to your invitee. You can also copy them below to share directly."
                : "We couldn't send the email automatically — please share these credentials with your invitee directly."}
            </DialogDescription>
          </DialogHeader>
          {justCreated && (
            <div className="space-y-3">
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 w-24 shrink-0 pt-1">Email</span>
                  <code className="text-sm text-gray-900 break-all" data-testid="sa-created-email">{justCreated.email}</code>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 w-24 shrink-0 pt-1">Temp password</span>
                  <code className="text-sm text-gray-900 break-all font-mono bg-white border border-indigo-200 rounded px-2 py-0.5" data-testid="sa-created-pwd">{justCreated.temp_password}</code>
                </div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900">
                <strong>Reminder:</strong> Your sub-agent can&rsquo;t log in until an admin approves them. You&rsquo;ll be notified once that happens.
              </div>
              <Button onClick={copyCreds} variant="outline" className="w-full" data-testid="sa-copy-creds-btn">
                <Copy size={14} className="mr-2" /> Copy credentials
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setJustCreated(null)} className="bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="sa-creds-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
